<template>
  <v-card
    flat
    class="rounded-2xl shadow-xl mx-auto"
    style="max-width:500px; min-width:340px; margin-top:40px;"
  >
    <!-- HEADER -->
    <v-card-title class="flex items-center gap-3">
      <v-row class="ma-0">
        <div>
          <div>
          <v-icon class="mr-2" size="26">mdi-calendar-check</v-icon>
          <span class="font-semibold text-lg">Detalle de Actividad</span>
          </div>
          <div class="flex items-center gap-2 mb-1">
            <v-chip color="primary" size="small" v-if="activity.type" label class="mr-2">
              <v-icon left size="16">mdi-email</v-icon>
              {{ activity.type }}
            </v-chip>
            <v-chip
              :color="statusColor(activity.status?.name)"
              size="small"
              label
              v-if="activity.status?.name"
            >
              {{ activity.status?.name }}
            </v-chip>
            
          </div>
        </div>
        <v-spacer />
        <v-btn icon variant="text" @click="$emit('close')" aria-label="Cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-row>
    </v-card-title>
    <v-divider />

    <!-- CHIPS Y SUJETO -->
    <v-card-text class="flex flex-col gap-2">
      <v-row class="ma-0">
        <div class="mb-3 text-md font-medium pt-2">
          {{ subjectName }}
          <span v-if="activity.contact" class="text-gray-400 text-sm">- {{ activity.contact.name }}</span>
        </div>
        <v-spacer/>
        <v-chip v-if="activity.subject" size="small" variant="outlined" color="secondary">
          {{ subjectLabel }}
        </v-chip>
      </v-row>
      <!-- FECHA y HORA -->
      <div class="flex items-center gap-3 mb-3 text-gray-500 text-sm">
        <v-icon size="18">mdi-clock-outline</v-icon>
        {{ formatDate(activity.date) }}
      </div>
    </v-card-text>

    <v-divider />

    <!-- DESCRIPCION -->
    <v-card-text>
      <div class="mb-3">
        <span class="font-medium text-sm text-gray-500 text-caption">Descripción</span>
        <div class="mt-1">{{ activity.description || 'Sin descripción' }}</div>
      </div>
      <div class="mb-3" v-if="activity.additional_data?.result">
        <span class="font-medium text-sm text-gray-500 text-caption">Resultado</span>
        <div class="mt-1">{{ activity.additional_data.result }}</div>
      </div>
      <div class="mb-3" v-if="activity.additional_data?.documents?.length">
        <span class="font-medium text-sm text-gray-500 text-caption">Evidencias</span>
        <div class="flex flex-wrap gap-2 mt-2">
          <template v-for="(doc, i) in activity.additional_data.documents" :key="i">
            <v-chip
              v-if="doc.file"
              size="small"
              color="success"
              @click="openEvidence(doc.file)"
              style="cursor:pointer"
              prepend-icon="mdi-paperclip"
            >{{ doc.label || `Documento ${i+1}` }}</v-chip>
          </template>
        </div>
      </div>
      <div class="mb-3">
        <span class="font-medium text-sm text-gray-500 text-caption">Responsable</span>
        <div class="mt-1 flex items-center gap-2">
          <v-avatar v-if="activity.user?.profile_photo_path" size="24" class="mr-1">
            <v-img :src="activity.user.profile_photo_path" />
          </v-avatar>
          {{ responsibleName }}
        </div>
      </div>
    </v-card-text>

    <v-divider />

    <!-- ACCIONES -->
    <v-card-actions class="flex justify-between gap-2 px-4 py-2">
      <v-btn color="success" variant="tonal" size="small" @click="onFinishActivity" :disabled="loading || activity.completed_at">
        <v-icon left size="18">mdi-check-circle</v-icon>
        Terminar
      </v-btn>
      
      <v-btn color="primary" variant="outlined" size="small" @click="onReschedule" :disabled="loading">
        <v-icon left size="18">mdi-calendar-refresh</v-icon>
        Reagendar
      </v-btn>

      <v-btn v-if="$can('activities.update')" variant="outlined" size="small" @click="onEditActivity" :disabled="loading">
        <v-icon left size="18">mdi-pencil</v-icon>
        Editar
      </v-btn>
    </v-card-actions>

    <!-- LOADING OVERLAY -->
    <v-overlay :model-value="loading" persistent>
      <v-progress-circular indeterminate size="48" color="primary" />
    </v-overlay>

  </v-card>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useActivitiesStore } from '@/store/activities';
import { useFloatFormStore } from '@/store/floatForm';
import { EVIDENCE_FIELDS } from '@/components/modules/activities/activities.config';
import { EDIT_FORM } from '@/components/modules/activities/activities.config';
import { VTextarea } from 'vuetify/components';

import { format } from 'date-fns';

const props = defineProps<{ id: number }>();
const emit = defineEmits(['close', 'edit', 'finish', 'reschedule']);
const store = useActivitiesStore();
const floatFormStore = useFloatFormStore();

const loading = computed(() => store.activity === null);

async function fetchActivity() {
  await store.fetchActivityById(props.id);
}

onMounted(fetchActivity);

watch(
  () => props.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      await fetchActivity();
    }
  }
);

// Computed para los datos a mostrar
const activity = computed(() => store.activity || {}) as any; // Así se vuelve reactivo al cambio en el store

const subjectLabel = computed(() => {
  if (!activity.value?.subject) return '';
  return activity.value.subject.type === 'clients' ? 'Cliente' : 'Lead';
});
const subjectName = computed(() => {
  const attr = activity.value?.subject?.attributes || {};
  return attr.commercial_name || attr.legal_name || '';
});
const responsibleName = computed(() => {
  const u = activity.value?.user || {};
  return [u.name, u.last_name].filter(Boolean).join(' ') || '-';
});
function formatDate(dateStr: string) {
  if (!dateStr) return '-';
  return format(new Date(dateStr), "eee dd 'de' MMMM yyyy, h:mm a", { locale: undefined });
}
function statusColor(status?: string) {
  switch (status) {
    case 'Vencida': return 'red';
    case 'Completada': return 'green';
    case 'Pendiente': return 'orange';
    default: return 'grey';
  }
}
function openEvidence(url: string) {
  window.open(url, '_blank');
}

async function onReschedule() {
  try {
    const REASON_FIELD = {
      value: 'reason',
      label: 'Motivo de Reagendamiento',
      component: VTextarea,
      required: true,
      col: 12,
      rows: 3,
      autoGrow: true,
    };
    const fields = [...EDIT_FORM, REASON_FIELD];
    const formData = await floatFormStore.openFormWithPromise({
      component: 'EditForm',
      formTitle: 'Reagendar Actividad',
      formWidth: 700,
      module: 'Activities',
      props: {
        fields,
        initialValues: { ...activity.value, reason: '' }
      }
    });
    await store.rescheduleActivity(activity.value.id, formData);
    await fetchActivity();
  } catch (e) {
    console.warn('Reagendamiento cancelado o error', e);
  }
}

async function onFinishActivity() {
  try {
    const formData = await floatFormStore.openFormWithPromise({
      component: 'EvidenceForm',
      formTitle: 'Finalizar actividad',
      formWidth: 600,
      props: { fields: EVIDENCE_FIELDS }
    });
    await store.updateActivity(activity.value.id, {
      completed_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
      additional_data: {
        ...(activity.value.additional_data || {}),
        ...formData
      }
    });
    // Actualiza el detalle desde el store
    await fetchActivity();
  } catch (e) {
    console.warn('Cancelado o error', e);
  }
}

function onEditActivity() {
  floatFormStore.openForm({
    component: 'EditForm',
    formTitle: 'Editar actividad',
    formWidth: 800,
    module: 'Activities',
    props: {
      item: activity.value,
      onUpdate: (data: Record<string, any>) => store.updateActivity(data.id, data)
    }
  });
}
</script>


<style scoped>
.text-caption {
  font-size: 0.75rem;
  font-weight: 500;
}
</style>
